import { useSyncExternalStore } from "react";

/**
 * Connectivity is a hint from the browser plus evidence from real requests. The app is "writable"
 * only when the browser reports online AND the last request reached the server.
 */
interface State {
  browserOnline: boolean;
  serverReachable: boolean;
  /** At least one screen is showing data served from the private offline cache. */
  servingCached: boolean;
}

let state: State = {
  browserOnline: typeof navigator === "undefined" ? true : navigator.onLine,
  serverReachable: true,
  servingCached: false,
};
const listeners = new Set<() => void>();
const reconnectListeners = new Set<() => void>();
let probeTimer: number | undefined;

function set(next: Partial<State>) {
  const merged = { ...state, ...next };
  if (merged.browserOnline === state.browserOnline && merged.serverReachable === state.serverReachable && merged.servingCached === state.servingCached) return;
  const wasWritable = isWritable(state);
  state = merged;
  if (!wasWritable && isWritable(state)) {
    state = { ...state, servingCached: false };
    for (const fn of reconnectListeners) fn();
  }
  for (const fn of listeners) fn();
  scheduleProbe();
}

function isWritable(s: State) {
  return s.browserOnline && s.serverReachable;
}

function scheduleProbe() {
  if (typeof window === "undefined") return;
  window.clearTimeout(probeTimer);
  if (state.browserOnline && !state.serverReachable) {
    probeTimer = window.setTimeout(async () => {
      try {
        const res = await fetch("/api/config", { cache: "no-store" });
        if (res.ok) set({ serverReachable: true });
        else scheduleProbe();
      } catch {
        scheduleProbe();
      }
    }, 8000);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => set({ browserOnline: true, serverReachable: true }));
  window.addEventListener("offline", () => set({ browserOnline: false }));
}

export const connectivity = {
  get: () => state,
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  onReconnect(fn: () => void) {
    reconnectListeners.add(fn);
    return () => reconnectListeners.delete(fn);
  },
  reportNetworkFailure() {
    set({ serverReachable: false });
  },
  reportServerReached() {
    set({ serverReachable: true });
  },
  reportServedFromCache() {
    set({ servingCached: true });
  },
  isWritable: () => isWritable(state),
};

export function useConnectivity() {
  return useSyncExternalStore(connectivity.subscribe, connectivity.get, connectivity.get);
}

/** Shared write-guard: every mutation control consults this. */
export function useCanWrite() {
  const s = useConnectivity();
  return isWritable(s);
}

export const OFFLINE_WRITE_REASON = "You're offline. Reconnect to make changes.";
